import type { BroadcastFilters } from "@school-sales-ai/shared";
import { useCatalogContext } from "@/hooks/useCatalog";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";
import { useLeadAssignmentPolicy } from "@/hooks/useLeadAssignment";

/**
 * "A, B y C" for a list of names, with the "none" sentinel spelled as `noneLabel`
 * and any id the catalog no longer lists as `unknownLabel` (archived rows).
 */
function nameList(
  ids: string[],
  nameOf: Map<string, string>,
  noneLabel: string,
  unknownLabel: string,
): string {
  return ids
    .map((id) => (id === "none" ? noneLabel : (nameOf.get(id) ?? unknownLabel)))
    .join(", ");
}

/**
 * The active filters, spelled out in words.
 *
 * Load-bearing rather than decorative: the Kanban board applies its stage filter
 * client-side by hiding columns, so this line is the only place a human can see
 * whether the column they narrowed to is actually part of the send. A count
 * alone cannot show that.
 */
export function BroadcastFilterChips({
  filters,
}: {
  filters: BroadcastFilters;
}): React.JSX.Element {
  const { data: stages } = usePipelineStagesQuery();
  const { data: catalog } = useCatalogContext();
  const stageNames = new Map((stages ?? []).map((s) => [s.key, s.label]));
  const productNames = new Map((catalog?.products ?? []).map((p) => [p.id, p.name]));
  const cycleNames = new Map((catalog?.cycles ?? []).map((c) => [c.id, c.label]));
  const { data: assignment } = useLeadAssignmentPolicy();
  const memberNames = new Map((assignment?.members ?? []).map((m) => [m.id, m.name]));

  // Names, never "Programa filtrado": this is the last screen before the send,
  // and with several values per filter a count tells nobody which ones.
  const chips: string[] = [];
  if (filters.stages?.length)
    chips.push(
      `${filters.stages.length > 1 ? "Etapas" : "Etapa"}: ${filters.stages
        .map((key) => stageNames.get(key) ?? key)
        .join(", ")}`,
    );
  if (filters.channel)
    chips.push(`Canal: ${filters.channel === "widget" ? "Sitio web" : "WhatsApp"}`);
  if (filters.search) chips.push(`Búsqueda: "${filters.search}"`);
  if (filters.scoreMin !== undefined) chips.push(`Score ≥ ${String(filters.scoreMin)}`);
  if (filters.scoreMax !== undefined) chips.push(`Score ≤ ${String(filters.scoreMax)}`);
  if (filters.productIds?.length)
    chips.push(
      `${filters.productIds.length > 1 ? "Programas" : "Programa"}: ${nameList(
        filters.productIds,
        productNames,
        "sin programa",
        "programa archivado",
      )}`,
    );
  if (filters.cycleIds?.length)
    chips.push(
      `${filters.cycleIds.length > 1 ? "Ciclos" : "Ciclo"}: ${nameList(
        filters.cycleIds,
        cycleNames,
        "sin ciclo",
        "ciclo archivado",
      )}`,
    );
  if (filters.labelIds?.length)
    chips.push(`${String(filters.labelIds.length)} etiqueta(s)`);
  if (filters.assigneeIds?.length)
    chips.push(
      `Asignado a: ${nameList(filters.assigneeIds, memberNames, "sin asignar", "otra persona")}`,
    );
  if (filters.from) chips.push(`Desde ${filters.from}`);
  if (filters.to) chips.push(`Hasta ${filters.to}`);
  if (filters.goneCold) chips.push("Sin actividad reciente");

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.length === 0 ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          Sin filtros: todos los prospectos activos
        </span>
      ) : (
        chips.map((chip) => (
          <span
            key={chip}
            className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700"
          >
            {chip}
          </span>
        ))
      )}
    </div>
  );
}
